import { supabase } from "./supabase";
import { getLessonAnchorDate } from "../utils/lessonDate";

const STORAGE_BUCKET = "spiritual-content";

const FILE_PATH =
  "sabbath-school/current.json";

/* ────────────────────────────────────────────────────────── */
/* GET CURRENT QUARTER                                        */
/* ────────────────────────────────────────────────────────── */

const QUARTER_LABELS = ["", "Ene–Mar", "Abr–Jun", "Jul–Sep", "Oct–Dic"];

/**
 * stripMemoryLabel — quita SOLO al inicio del string el prefijo de etiqueta
 * "Para memorizar" o "Texto para memorizar" (algunos trimestres de Adventech lo
 * traen metido dentro del texto del versículo). Conserva la cita «…» que sigue:
 * solo se elimina la etiqueta inicial, nunca el versículo.
 *
 * Si el string NO empieza con ese prefijo (otros trimestres/idiomas pueden no
 * traerlo), se devuelve idéntico — no se mutila nada.
 */
export function stripMemoryLabel(text) {
  if (typeof text !== "string") return text;
  return text.replace(/^\s*(?:texto\s+)?para\s+memorizar\s*[:.]?\s*/i, "");
}

// Month-based quarter — fallback only (used when the table can't be read).
function _monthTrimestre() {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `es-${now.getFullYear()}-${String(q).padStart(2, "0")}`;
}

/**
 * getCurrentTrimestre — single source of truth for "which quarter is active".
 *
 * Derives the quarter from REAL lesson data, not the calendar month: takes the
 * Sabbath School anchor Saturday (getLessonAnchorDate, which already encodes the
 * Saturday-noon rule) and finds the lesson whose fecha_inicio is the most recent
 * one on or before that anchor — that row's trimestre IS the active quarter.
 * This stays correct at quarter boundaries (e.g. the Q3 cycle that starts on a
 * Saturday still in calendar-June).
 *
 * Fallback: if the table returns empty/error (offline, not synced yet), fall
 * back to the month-based formula — same behavior as before this change.
 */
export async function getCurrentTrimestre() {
  try {
    const anchor = getLessonAnchorDate();
    const { data, error } = await supabase
      .from("escuela_sabatica_lecciones")
      .select("trimestre")
      .lte("fecha_inicio", anchor)
      .order("fecha_inicio", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error && data?.trimestre) return data.trimestre;
  } catch { /* fall through to month-based fallback */ }
  return _monthTrimestre();
}

// Primary: reads from escuela_sabatica_lecciones (Supabase DB)
async function _getCurrentFromDB() {
  const trimestre = await getCurrentTrimestre();
  // trimestre format: "es-YYYY-QQ" → derive year/quarter for the display title.
  const [, yStr, qStr] = trimestre.split("-");
  const year = Number(yStr);
  const q    = Number(qStr);

  const { data: lessons, error } = await supabase
    .from("escuela_sabatica_lecciones")
    .select("numero, titulo, fecha_inicio, texto_clave, dias")
    .eq("trimestre", trimestre)
    .order("numero", { ascending: true });

  if (error || !lessons?.length) return null;

  return {
    quarter: {
      title:     `Escuela Sabática · ${QUARTER_LABELS[q]} ${year}`,
      startDate: lessons[0].fecha_inicio,
    },
    weeks: lessons.map((l) => ({
      title:          l.titulo,
      memoryVerse:    l.texto_clave ?? null,
      memoryVerseRef: "",
      // Array de días tal como viene de la tabla: { dia, fecha, titulo, bloques }.
      // El día de hoy se elige por match de fecha en getTodayLessonTitle.
      days:           Array.isArray(l.dias) ? l.dias : [],
    })),
  };
}

// Fallback: original Storage JSON (spiritual-content/sabbath-school/current.json)
async function _getCurrentFromStorage() {
  const { data } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(FILE_PATH);

  const response = await fetch(data.publicUrl);
  if (!response.ok) throw new Error("No se pudo cargar la lección");
  return await response.json();
}

export async function getCurrentSabbathSchool() {
  try {
    const plan = await _getCurrentFromDB();
    if (plan) return plan;
  } catch { /* fall through to storage */ }

  try {
    return await _getCurrentFromStorage();
  } catch (error) {
    console.error("Error loading sabbath school:", error);
    return null;
  }
}

/* ────────────────────────────────────────────────────────── */
/* LOCAL DATE PARSER                                          */
/* ────────────────────────────────────────────────────────── */

// Parse a "YYYY-MM-DD" string as local midnight, not UTC midnight.
// new Date("YYYY-MM-DD") parses as UTC 00:00, which in negative-offset
// timezones (e.g. UTC-6) equals the previous local day — causing the
// week counter to advance one day too early.
function parseLocalDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/* ────────────────────────────────────────────────────────── */
/* SATURDAY MORNING PASTORAL EXCEPTION                        */
/* ────────────────────────────────────────────────────────── */

/**
 * getEffectiveDiffDays — returns the number of full days elapsed since
 * startDate for the purpose of determining the current lesson week index.
 *
 * Pastoral exception: on Saturday before noon (12:00 PM), the week rollover
 * is delayed by 12 hours. This ensures the Sabbath School review lesson of
 * the completed week stays visible during Saturday morning services —
 * the time when the weekly lesson is typically reviewed in church.
 *
 * At exactly 12:00 PM or later on Saturday, the counter advances normally
 * to the next week. On all other days, midnight is the changeover boundary.
 *
 * Uses the device's local timezone throughout to match pastoral context.
 */
export function getEffectiveDiffDays(startDate) {
  const now = new Date();

  // Saturday = 6 (getDay); "morning" = before noon
  const isSaturdayMorning = now.getDay() === 6 && now.getHours() < 12;

  // On Saturday morning, roll back 12 h so the week counter stays in the
  // previous cycle. The day key still reads "saturday" (see getTodayLessonTitle).
  const effectiveNow = isSaturdayMorning
    ? new Date(now.getTime() - 12 * 60 * 60 * 1000)
    : now;

  return Math.floor(
    (effectiveNow - startDate) / (1000 * 60 * 60 * 24)
  );
}

/* ────────────────────────────────────────────────────────── */
/* GET TODAY LESSON                                           */
/* ────────────────────────────────────────────────────────── */

export function getTodayLessonTitle(plan) {

  if (
    !plan ||
    !plan.quarter ||
    !plan.quarter.startDate ||
    !Array.isArray(plan.weeks)
  ) {
    console.warn("[SabbathSchool] guard failed:", {
      hasPlan: !!plan,
      hasQuarter: !!plan?.quarter,
      hasStartDate: !!plan?.quarter?.startDate,
      weeksIsArray: Array.isArray(plan?.weeks),
    });
    return null;
  }

  const startDate = parseLocalDate(plan.quarter.startDate);

  // Use the pastoral-exception-aware diff for week selection
  const diffDays = getEffectiveDiffDays(startDate);

  // Prevent negative values (before quarter start)
  if (diffDays < 0) return null;

  const weekIndex = Math.floor(diffDays / 7);
  const currentWeek = plan.weeks?.[weekIndex];

  if (!currentWeek) return null;

  // Día de hoy: match por FECHA real sobre el array de días (misma lógica que el
  // lado miembro en LeccionDia), no por nombre de día de semana. Si no hay match
  // (p.ej. sábado por la mañana, cuando weekIndex apunta a la semana previa),
  // lessonTitle cae a null y el componente muestra solo el título semanal.
  const now = new Date();
  const hoyISO =
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const todayLesson = Array.isArray(currentWeek.days)
    ? currentWeek.days.find((d) => d.fecha === hoyISO)
    : null;

  return {
    quarterTitle:    plan.quarter.title,
    weekTitle:       currentWeek.title,
    lessonTitle:     todayLesson?.titulo || null,
    memoryVerse:     currentWeek.memoryVerse,
    memoryVerseRef:  currentWeek.memoryVerseRef,
  };
}
